// Form is based on Formik
// Data validation is based on Yup
// Please, be familiar with article first:
// https://hackernoon.com/react-form-validation-with-formik-and-yup-8b76bda62e10
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
} from '@mui/material';
import { green } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import { FieldArray, FormikProps } from 'formik';
import React, { ChangeEvent, useMemo, useRef, useState } from 'react';
import { Link, Route, Switch } from 'react-router-dom';
import { useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import { MaterialAutocomplete } from '../../../common/formik-material-ui';
import '../../../common/formik-material-ui/commaSeparatedEmails';
import { trackAddPowerListTeam } from '../../../common/userTracking';
import {
  Powerlist,
  PowerlistTeam,
} from '../../../lib/kixie-entities/tables/Powerlist';
import {
  methodNameEnum,
  refetchToggle,
} from '../../../recoil/selectors/makePHPRequest';
import {
  IPermissions,
  permissionsSelector,
} from '../../../recoil/selectors/permissionsSelector';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../../services/rest/makeAPIRequest';
import { DefaultActionSkeleton } from '../../components/dropdowns/DefaultAction';
import { useSnackbarWithStyles } from '../../components/notifications/SnackbarProviderWithStyles';
import { ViewQueueTeam } from '../../pages/EditQueueTeam';

const useStyles = makeStyles((theme: Theme) => ({
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
  },
  table: {
    marginTop: theme.spacing(2), // align with fields
    marginBottom: theme.spacing(2), // align with fields
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  fabProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -22,
    marginLeft: -22,
  },
}));

interface PowerlistTeams {
  id: number | null;
  formik: FormikProps<Powerlist>;
  isAgentPowerlist: number | undefined;
  onTeamChange: () => void;
}

export const Members = ({
  id,
  formik,
  isAgentPowerlist,
  onTeamChange,
}: PowerlistTeams) => {
  const classes = useStyles();
  const [autocomplete, setAutocomplete] = useState<any>([]);
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const { canManage } = permissionsLoadable.contents as IPermissions;
  const businessTeams = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_TEAMS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_TEAMS,
      }),
    })
  );
  // get businessTeams that do not belong to queue
  const teamsNotInPowerlist = useRef<any>([]);
  teamsNotInPowerlist.current = useMemo(
    () =>
      businessTeams.state === 'loading'
        ? teamsNotInPowerlist.current
        : businessTeams.contents?.response?.data?.result
            ?.filter(
              (businessTeam: any) =>
                !formik.values.powerlistTeams?.find(
                  (team: PowerlistTeam) => team.teamId === businessTeam.teamid
                )
            )
            ?.sort((a: any, b: any) =>
              a?.teamname
                ?.toLowerCase()
                ?.localeCompare(b?.teamname?.toLowerCase())
            )
            .map((elem: any) => ({ ...elem, teamId: elem?.teamid })) ?? [],
    [businessTeams, formik.values.powerlistTeams]
  );

  /**
   * Change field with new value
   * @param name Name of field (if nested then last value is type)
   */
  const handleChangeForAutocomplete = (
    event: ChangeEvent<unknown>,
    newValue: any[]
  ) => {
    // keep autocomplete in sync
    setAutocomplete(newValue);
  };
  const setRefetchToggleForBusinessTeams = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_TEAMS)
  );
  const [rowForEdit, handleRowForEdit] = useState({} as { teamId: number });
  const [teamEditModal, handleTeamEditModal] = useState(false);
  const handleEdit = (row) => {
    handleTeamEditModal(true);
    handleRowForEdit(row);
  };
  // notification using snackbar
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  return (
    <>
      {/* alignItems: 'center', // set native height for button since flex sets height to be the same for all items */}
      {!isAgentPowerlist && (
        <MaterialAutocomplete
          className={`flex-grow-1 ${classes.field}`}
          type="text"
          label="Teams"
          size="small"
          variant="outlined"
          options={teamsNotInPowerlist.current}
          value={autocomplete}
          multiple={true}
          onChange={handleChangeForAutocomplete}
          getOptionLabel={(option) => option.teamname ?? ''}
          isOptionEqualToValue={(option, value) =>
            option?.teamid === value?.teamid
          }
          disabled={businessTeams.state === 'loading'} // in progress or queue loading
        />
      )}
      <Switch>
        <Route path="/manage/powerlists/:id/edit">
          {({ history, match }) => (
            <ViewQueueTeam
              show={!!teamEditModal}
              setRefetchToggleForBusinessTeams={
                setRefetchToggleForBusinessTeams
              }
              id={rowForEdit.teamId}
              onHide={() => {
                handleTeamEditModal(false);
                setRefetchToggleForBusinessTeams({});
                onTeamChange();
              }}
              enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
            />
          )}
        </Route>
      </Switch>
      <FieldArray
        name="powerlistTeams"
        render={(arrayHelper) => (
          <>
            {!isAgentPowerlist && (
              <Button
                className={classes.field}
                variant="contained"
                color="primary"
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                  if (window.location.pathname === '/manage/powerlists/add') {
                    trackAddPowerListTeam();
                  }
                  autocomplete.forEach((item: PowerlistTeam) => {
                    item.powerlistTeamId = null;
                    arrayHelper.push(item);
                  });
                  setAutocomplete([]);
                }}
                disableElevation
                disabled={!canManage}
              >
                Add
              </Button>
            )}
            <Table className={classes.table} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell valign="baseline">Team</TableCell>
                  <TableCell valign="baseline">Member Count</TableCell>
                  {!isAgentPowerlist && (
                    <TableCell align="right" valign="baseline" width="100">
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {(businessTeams.state === 'loading' ||
                  !formik.values.powerlistTeams) && (
                  <TableRow>
                    <TableCell>
                      <DefaultActionSkeleton />
                    </TableCell>
                    <TableCell>
                      <DefaultActionSkeleton />
                    </TableCell>
                    {!isAgentPowerlist && (
                      <TableCell>
                        <DefaultActionSkeleton />
                      </TableCell>
                    )}
                  </TableRow>
                )}
                {formik.values.powerlistTeams?.map(
                  (team: PowerlistTeam, index: number) => (
                    <TableRow key={team.teamId}>
                      {!isAgentPowerlist && (
                        <TableCell valign="baseline">
                          <Link onClick={() => handleEdit(team)} to="#">
                            {team.teamname}
                          </Link>
                        </TableCell>
                      )}
                      {!!isAgentPowerlist && (
                        <TableCell valign="baseline">{team.teamname}</TableCell>
                      )}
                      <TableCell valign="baseline">
                        {team.memberCount}
                      </TableCell>
                      {!isAgentPowerlist && (
                        <TableCell
                          align="right"
                          valign="baseline"
                          padding="none"
                        >
                          <IconButton
                            aria-label="delete"
                            disabled={!canManage}
                            onClick={(event) => {
                              arrayHelper.remove(index);
                            }}
                            className={classes.deleteIconButton}
                            size="large"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </>
        )}
      />
    </>
  );
};
